import {
  ActionCategory,
  type FullStagefulAction,
  PermissionLevel,
  StagefulAction,
  StagelessAction,
  StageName,
  stageNames,
  type WorkPacketAction,
  type WorkPacketActionName,
} from "src/types/work-packets";
import { capitalize } from "src/utils/capitalize";
import { WorkPacketType } from "../WorkPacketType";
import { FeatureFlag } from "src/featureFlags/featureFlags";

const actionObjectCaches: Record<WorkPacketType, Map<WorkPacketActionName, WorkPacketAction>> = {
  [WorkPacketType.CHARGEBACKS]: new Map(),
  [WorkPacketType.SHORTAGES]: new Map(),
  [WorkPacketType.ACCRUALS]: new Map(),
};

/*
 * TODO(daniel): consolidate all work packet action definition code under one file.
 *
 * Action definitions are used for three use cases:
 * - Displaying (in table and details popup)
 * - Filtering (in table filter section)
 * - Updating (directly in table, or in details poup)
 */

type ActionVariant = Partial<Omit<ActionDescriptor, "variants">>;
type ActionVariants = Partial<Record<WorkPacketType, ActionVariant>>;

interface ActionDescriptor {
  category: ActionCategory;
  title: string;
  color: string;
  onlyAdminCanSet?: "first-stage" | "any";
  onlySupervisorCanSet?: "first-stage" | "any";
  excludeFirstStageForAccruals?: boolean;
  onlyOnWorkPacketTypes?: WorkPacketType[];
  customStages?: StageName[];
  variants?: ActionVariants;
  featureFlag?: FeatureFlag;
}

const reopenStages = [StageName.First, StageName.Second];

const stagefulActionDescriptors: Record<StagefulAction, ActionDescriptor> = {
  [StagefulAction.Identified]: {
    category: ActionCategory.Dispute,
    title: "Identified",
    color: "#FFD878",
    variants: {
      [WorkPacketType.CHARGEBACKS]: {
        title: "Chargeback Identified",
        onlyAdminCanSet: "first-stage",
      },
      [WorkPacketType.SHORTAGES]: {
        title: "Shortage Identified",
        onlyAdminCanSet: "first-stage",
      },
      [WorkPacketType.ACCRUALS]: {
        title: "Accrual Identified",
        onlySupervisorCanSet: "first-stage",
      },
    },
  },
  [StagefulAction.DisputeReidentified]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Reidentified",
    color: "#FFD878",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS],
    onlyAdminCanSet: "any",
  },
  [StagefulAction.DisputeFailed]: {
    category: ActionCategory.Dispute,
    title: "Dispute Failed",
    color: "#ff0000",
  },
  [StagefulAction.DipusteReattemptFailed]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Reattempt Failed",
    color: "#E03741",
    onlyAdminCanSet: "any",
  },
  [StagefulAction.DisputeCreated]: {
    category: ActionCategory.Dispute,
    title: "Dispute Created",
    color: "#FFCD4D",
  },
  [StagefulAction.DisputeInReview]: {
    category: ActionCategory.Dispute,
    title: "Dispute In Review",
    color: "#FFCD4D",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS],
  },
  [StagefulAction.DisputeApproved]: {
    category: ActionCategory.Dispute,
    title: "Dispute Resolved - Approved",
    color: "#39D4A5",
  },
  [StagefulAction.DisputePartiallyApproved]: {
    category: ActionCategory.Dispute,
    title: "Dispute Resolved - Partially Approved",
    color: "#318FFF",
    excludeFirstStageForAccruals: true,
  },
  [StagefulAction.DisputeNeedsMoreInfo]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Needs More Info",
    color: "#B25B0A",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS],
  },
  [StagefulAction.DisputeDenied]: {
    category: ActionCategory.Dispute,
    title: "Dispute Resolved - Denied",
    color: "#FF820F",
  },
  [StagefulAction.DisputeExpired]: {
    category: ActionCategory.Dispute,
    title: "Dispute Expired",
    color: "#EAECF0",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS],
  },
  [StagefulAction.ReopenCreated]: {
    category: ActionCategory.Reopen,
    title: "Reopen Created",
    color: "#FFCD4D",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
    customStages: reopenStages,
    featureFlag: FeatureFlag.Reopens,
  },
  [StagefulAction.ReopenFailed]: {
    category: ActionCategory.Reopen,
    title: "Reopen Failed",
    color: "#ff0000",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
    customStages: reopenStages,
    featureFlag: FeatureFlag.Reopens,
  },
  [StagefulAction.ReopenApproved]: {
    category: ActionCategory.Reopen,
    title: "Reopen Approved",
    color: "#39D4A5",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
    customStages: reopenStages,
    featureFlag: FeatureFlag.Reopens,
  },
  [StagefulAction.ReopenPartiallyApproved]: {
    category: ActionCategory.Reopen,
    title: "Reopen Partially Approved",
    color: "#318FFF",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
    customStages: reopenStages,
    featureFlag: FeatureFlag.Reopens,
  },
  [StagefulAction.ReopenDenied]: {
    category: ActionCategory.Reopen,
    title: "Reopen Denied",
    color: "#FF820F",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
    customStages: reopenStages,
    featureFlag: FeatureFlag.Reopens,
  },

  [StagefulAction.EscalationApproved]: {
    category: ActionCategory.Escalation,
    title: "Escalation - Approved",
    color: "#39D4A5",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.SHORTAGES],
  },
  [StagefulAction.EscalationPartiallyApproved]: {
    category: ActionCategory.Escalation,
    title: "Escalation - Partially Approved",
    color: "#318FFF",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.SHORTAGES],
  },
  [StagefulAction.EscalationDenied]: {
    category: ActionCategory.Escalation,
    title: "Escalation - Denied",
    color: "#E03741",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.SHORTAGES],
  },
  [StagefulAction.NotPursuing]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Not Pursuing",
    color: "#EAECF0",
    onlyOnWorkPacketTypes: [WorkPacketType.ACCRUALS],
  },
  [StagefulAction.MonitorRemittance]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Monitor Remittance",
    color: "#adba1a",
    onlyOnWorkPacketTypes: [WorkPacketType.ACCRUALS],
  },
  [StagefulAction.EscalationIdentified]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Escalation Identified (RS level)",
    color: "#dbc64d",
    onlyOnWorkPacketTypes: [WorkPacketType.ACCRUALS],
  },
  [StagefulAction.EscalatedInternal]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Escalated Internal (Sup)",
    color: "#80760f",
    onlyOnWorkPacketTypes: [WorkPacketType.ACCRUALS],
  },
  [StagefulAction.EscalatedAmazon]: {
    category: ActionCategory.Dispute,
    title: "Dispute - Escalated Amazon (Sup)",
    color: "#4a4511",
    onlyOnWorkPacketTypes: [WorkPacketType.ACCRUALS],
  },
};

const stagelessActionDescriptors: Record<StagelessAction, ActionDescriptor> = {
  [StagelessAction.ReadyForReopen]: {
    category: ActionCategory.Reopen,
    title: "Ready For Reopen",
    color: "#7B3DFF",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
    featureFlag: FeatureFlag.Reopens,
  },
  [StagelessAction.ReadyForReview]: {
    category: ActionCategory.PreCase,
    title: "Ready For Review",
    color: "#7B3DFF",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.SHORTAGES],
  },
  [StagelessAction.ReadyForOssr]: {
    category: ActionCategory.PreCase,
    title: "Ready For OSSR",
    color: "#7B3DFF",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
  },
  [StagelessAction.NotPursuing]: {
    category: ActionCategory.PreCase,
    title: "Not Pursuing",
    color: "#EAECF0",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.SHORTAGES],
  },
  [StagelessAction.WaitingOnAmazon]: {
    category: ActionCategory.PreCase,
    title: "Waiting On Amazon",
    color: "#EAECF0",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.SHORTAGES],
  },
  [StagelessAction.Investigate]: {
    category: ActionCategory.PreCase,
    title: "Investigate",
    color: "#FFCD4D",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
  },

  [StagelessAction.WaitingForPayment]: {
    category: ActionCategory.Case,
    title: "Waiting for Payment",
    color: "#FFCD4D",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
  },

  [StagelessAction.CasePaidOut]: {
    category: ActionCategory.Case,
    title: "Case Paid Out",
    color: "#FFCD4D",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
  },

  [StagelessAction.PaidSettlementNoPursue]: {
    category: ActionCategory.Case,
    title: "Paid Settlement No Pursue",
    color: "#FFCD4D",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
  },

  [StagelessAction.Junk]: {
    category: ActionCategory.OtherActions,
    title: "Junk",
    color: "#E03741",
    onlyOnWorkPacketTypes: [WorkPacketType.CHARGEBACKS, WorkPacketType.ACCRUALS],
  },
  [StagelessAction.Duplicate]: {
    category: ActionCategory.OtherActions,
    title: "Duplicate",
    color: "#EAECF0",
  },
  [StagelessAction.Expired]: {
    category: ActionCategory.OtherActions,
    title: "Expired",
    color: "#EAECF0",
    onlyOnWorkPacketTypes: [WorkPacketType.ACCRUALS],
  },
  [StagelessAction.NotApplicable]: {
    category: ActionCategory.OtherActions,
    title: "Not Applicable",
    color: "#EAECF0",
  },
  [StagelessAction.Abandoned]: {
    category: ActionCategory.OtherActions,
    title: "Abandoned",
    color: "#9CA3AF",
    onlyOnWorkPacketTypes: [WorkPacketType.SHORTAGES],
  },
  [StagelessAction.SkippedValueBelowMinimum]: {
    category: ActionCategory.OtherActions,
    title: "Skipped - Value below Minimum",
    color: "#EAECF0",
  },
};

const categoryLabels: Record<ActionCategory, string> = {
  [ActionCategory.Dispute]: "Dispute",
  [ActionCategory.Reopen]: "Reopen",
  [ActionCategory.Escalation]: "Escalation",
  [ActionCategory.PreCase]: "Pre-Case",
  [ActionCategory.Case]: "Case",
  [ActionCategory.OtherActions]: "Other Actions",
};

export const workPacketStages: Record<WorkPacketType, StageName[]> = {
  [WorkPacketType.CHARGEBACKS]: [StageName.First, StageName.Second],
  [WorkPacketType.SHORTAGES]: [StageName.First],
  [WorkPacketType.ACCRUALS]: [StageName.First, StageName.Second, StageName.Third],
};

export const getStagefulActionName = (stage: StageName, action: StagefulAction): FullStagefulAction =>
  `${stage}_${action}`;

export function createStagefulActions(workPacketType: WorkPacketType): WorkPacketAction[] {
  const actionNames = Object.entries(stagefulActionDescriptors) as [StagefulAction, ActionDescriptor][];
  const normalStages = workPacketStages[workPacketType];
  return stageNames.flatMap(stageName =>
    actionNames
      .filter(([_actionName, descriptor]) => {
        const actionStages = descriptor.customStages ?? normalStages;
        if (!actionStages.includes(stageName)) {
          return false;
        }
        if (
          descriptor.excludeFirstStageForAccruals &&
          workPacketType === WorkPacketType.ACCRUALS &&
          stageName === StageName.First
        ) {
          return false;
        }
        if (descriptor.onlyOnWorkPacketTypes && !descriptor.onlyOnWorkPacketTypes.includes(workPacketType)) {
          return false;
        }
        return true;
      })
      .map(([actionName, _descriptor]) => createStagefulAction(stageName, actionName, workPacketType)),
  );
}

export function createStagelessActions(workPacketType: WorkPacketType): WorkPacketAction[] {
  const actionNames = Object.entries(stagelessActionDescriptors) as [StagelessAction, ActionDescriptor][];
  return actionNames
    .filter(
      ([_actionName, descriptor]) =>
        !descriptor.onlyOnWorkPacketTypes || descriptor.onlyOnWorkPacketTypes.includes(workPacketType),
    )
    .map(([actionName, _descriptor]) => createStagelessAction(actionName, workPacketType));
}

export function createActionsForWorkPacketType(workPacketType: WorkPacketType) {
  return [...createStagefulActions(workPacketType), ...createStagelessActions(workPacketType)];
}

const createUnknownAction = (actionName: string): WorkPacketAction => ({
  value: actionName as WorkPacketActionName,
  title: actionName,
  category: categoryLabels[ActionCategory.OtherActions],
  categoryKey: ActionCategory.OtherActions,
  color: "#EAECF0",
  minimumPermissionToSet: PermissionLevel.Default,
});

export function createStagefulAction(
  stageName: StageName,
  actionName: StagefulAction,
  workPacketType: WorkPacketType,
): WorkPacketAction {
  const capitalizedStageName = capitalize(stageName);
  const fullActionName = `${stageName}_${actionName}` as const;

  const baseDescriptor = stagefulActionDescriptors[actionName];
  if (!baseDescriptor) return createUnknownAction(fullActionName);

  const descriptorVariant = baseDescriptor.variants?.[workPacketType];
  const descriptor = descriptorVariant ? { ...baseDescriptor, ...descriptorVariant } : baseDescriptor;

  const onlyAdminCanSet =
    descriptor.onlyAdminCanSet === "any" ||
    (descriptor.onlyAdminCanSet === "first-stage" && stageName === StageName.First);

  const onlySupervisorCanSet =
    descriptor.onlySupervisorCanSet === "any" ||
    (descriptor.onlySupervisorCanSet === "first-stage" && stageName === StageName.First);

  const actionStages = descriptor.customStages ?? workPacketStages[workPacketType];
  const shouldPrefixWithStage = actionStages.length > 1 || !actionStages.includes(stageName);

  const action: WorkPacketAction = {
    value: fullActionName,
    title: descriptor.title,
    category: categoryLabels[descriptor.category],
    categoryKey: descriptor.category,
    color: descriptor.color,
    minimumPermissionToSet: onlyAdminCanSet
      ? PermissionLevel.Admin
      : onlySupervisorCanSet
        ? PermissionLevel.Supervisor
        : PermissionLevel.Default,
    featureFlag: descriptor.featureFlag,
  };

  return !shouldPrefixWithStage
    ? action
    : {
        ...action,
        title: `${capitalizedStageName} ${action.title}`,
        category: `${capitalizedStageName} ${action.category}`,
      };
}

export function getStagelessActionValue(actionName: StagelessAction): WorkPacketActionName {
  return actionName;
}

export function createStagelessAction(actionName: StagelessAction, workPacketType: WorkPacketType): WorkPacketAction {
  const baseDescriptor = stagelessActionDescriptors[actionName];
  if (!baseDescriptor) return createUnknownAction(actionName);

  const descriptorVariant = baseDescriptor.variants?.[workPacketType];
  const descriptor = descriptorVariant ? { ...baseDescriptor, ...descriptorVariant } : baseDescriptor;

  const action: WorkPacketAction = {
    value: actionName,
    title: descriptor.title,
    category: categoryLabels[descriptor.category],
    categoryKey: descriptor.category,
    color: descriptor.color,
    minimumPermissionToSet: PermissionLevel.Default,
    featureFlag: descriptor.featureFlag,
  };

  return action;
}

function createAction(actionName: string, workPacketType: WorkPacketType): WorkPacketAction {
  const [stageName, ...rest] = actionName.split("_");
  if (stageNames.includes(stageName as StageName)) {
    return createStagefulAction(stageName as StageName, rest.join("_") as StagefulAction, workPacketType);
  } else {
    return createStagelessAction(actionName as StagelessAction, workPacketType);
  }
}

export function getActionObject(actionName: WorkPacketActionName, workPacketType: WorkPacketType): WorkPacketAction {
  const cache = actionObjectCaches[workPacketType];
  const cached = cache.get(actionName);
  if (cached) return cached;

  const action = createAction(actionName, workPacketType);
  cache.set(actionName, action);
  return action;
}
